/**
 * MemberLogin - phone + password login page for church members.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Phone, Lock, Eye, EyeOff, LogIn, Home } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

export default function MemberLogin() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setError('');
    if (!phone.trim() || !password) { setError('Phone and password required'); return; }
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/auth/member/login`, { phone, password });
      localStorage.setItem('member_token', res.data.token);
      localStorage.setItem('member_info', JSON.stringify(res.data.member));
      navigate('/');
    } catch (e) {
      setError(e.response?.data?.detail || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-950 via-purple-900 to-violet-950 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/15 rounded-full blur-3xl -translate-y-48 translate-x-48"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-violet-400/15 rounded-full blur-3xl translate-y-48 -translate-x-48"></div>
      <img src="/church-logo.png?v=2" alt="" className="absolute inset-0 m-auto w-[60vh] object-contain pointer-events-none opacity-[0.05]" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10">
        <div className="text-center mb-6">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 mx-auto mb-4 p-1 shadow-lg shadow-amber-500/30">
            <div className="w-full h-full rounded-full bg-violet-950 overflow-hidden">
              <img src="/images/church-logo.png?v=2" alt="CCC VOB" className="w-full h-full" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white">Member Login</h1>
          <p className="text-amber-200/80 text-sm mt-1">CCC VOB Parish</p>
        </div>

        <div className="bg-violet-950/60 backdrop-blur-xl rounded-3xl p-8 border border-amber-400/20 shadow-2xl">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/40 text-red-200 text-sm" data-testid="member-login-error">
              {error}
            </div>
          )}

          <label className="block text-sm font-medium text-amber-100 mb-2">Phone Number</label>
          <div className="relative mb-4">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400/60" />
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="07xxx xxxxxx"
              className="w-full bg-violet-900/50 border border-violet-700/60 rounded-xl pl-12 pr-4 py-3.5 text-white placeholder-violet-400 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
              data-testid="member-login-phone"
            />
          </div>

          <label className="block text-sm font-medium text-amber-100 mb-2">Password</label>
          <div className="relative mb-6">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400/60" />
            <input
              type={show ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Your password"
              onKeyPress={e => e.key === 'Enter' && submit()}
              className="w-full bg-violet-900/50 border border-violet-700/60 rounded-xl pl-12 pr-12 py-3.5 text-white placeholder-violet-400 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
              data-testid="member-login-password"
            />
            <button type="button" onClick={() => setShow(s => !s)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-amber-400/70 hover:text-amber-300">
              {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          <button onClick={submit} disabled={submitting}
            className="w-full py-4 rounded-xl bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-violet-950 font-bold text-lg shadow-lg shadow-amber-500/30 hover:from-amber-400 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            data-testid="member-login-submit">
            {submitting ? 'Signing in…' : (<><LogIn className="w-5 h-5" /><span>Sign In</span></>)}
          </button>

          <button onClick={() => navigate('/')}
            className="w-full mt-3 py-3 rounded-xl bg-violet-800/50 hover:bg-violet-800 text-amber-100 font-medium flex items-center justify-center gap-2 border border-violet-700/50">
            <Home className="w-4 h-4" /><span>Back to Homepage</span>
          </button>

          <p className="text-center text-amber-200/50 text-xs mt-6">
            Haven't received your password setup email?<br/>
            Please contact an admin to resend it.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
